
import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Poll } from "@/types/poll";
import { TypographySmall, TypographyMuted } from "@/components/ui/typography";
import PollDisplay from '@/components/polls/shared/PollDisplay';
import { useCentralizedVoting } from '@/hooks/voting/useCentralizedVoting';

interface LinkedPollsSectionProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  linkedPolls: Poll[];
  isLoading: boolean;
  onRefresh: () => void;
  canVote: boolean;
}

const LinkedPollsSection = ({
  isOpen,
  setIsOpen,
  linkedPolls,
  isLoading,
  onRefresh,
  canVote
}: LinkedPollsSectionProps) => {
  console.log("LinkedPollsSection rendering with polls:", linkedPolls.length);

  // Use centralized voting system
  const { submitVote } = useCentralizedVoting();

  // Enhanced vote handler with proper error handling
  const handleVote = async (pollId: string, optionId: number) => {
    const poll = linkedPolls.find(p => p.id === pollId);
    if (!poll) {
      console.error('Poll not found:', pollId);
      return;
    }
    
    try {
      const result = await submitVote(poll, optionId);
      if (result.success) {
        // Refresh polls after successful vote to get updated counts
        setTimeout(() => {
          onRefresh();
        }, 1000);
      }
    } catch (error) {
      console.error('Error voting in linked polls:', error);
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-4">
      <CollapsibleTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full flex items-center justify-between text-nation-purple hover:bg-nation-purple/10"
        >
          <span className="text-sm">
            {isLoading 
              ? 'Loading Poll Details...' 
              : `${linkedPolls.length} Poll${linkedPolls.length !== 1 ? 's' : ''} Available`
            }
          </span>
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4 space-y-4">
        {isLoading ? (
          <div className="text-sm text-muted-foreground py-2 text-center">
            Fetching linked polls...
          </div>
        ) : linkedPolls.length > 0 ? (
          <Accordion 
            type="single" 
            collapsible 
            className="w-full space-y-2" 
            defaultValue={linkedPolls[0].id}
          >
            {linkedPolls.map((poll) => {
              return (
                <AccordionItem 
                  key={poll.id} 
                  value={poll.id} 
                  className="border-nation-purple/20 hover:border-nation-purple/40"
                >
                  <AccordionTrigger className="text-left hover:bg-nation-purple/5 px-2 rounded">
                    <div className="flex flex-col items-start space-y-1">
                      <div className="font-medium text-nation-blue">{poll.title}</div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="mt-1">
                          {poll.status === 'live' ? (
                            <span className="text-green-600">Active</span>
                          ) : poll.status === 'closed' ? (
                            <span className="text-blue-600">Closed</span>
                          ) : (
                            <span className="text-gray-600">Draft</span>
                          )}
                        </Badge>
                        <TypographySmall className="text-muted-foreground mt-1">
                          {poll.total_votes} {poll.total_votes === 1 ? 'vote' : 'votes'}
                        </TypographySmall>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="mt-2 border-t pt-3 border-border">
                      <PollDisplay 
                        poll={{...poll, title: ''}} // Remove the title to avoid duplication
                        canVote={canVote}
                        onVote={(optionId: number) => handleVote(poll.id, optionId)}
                        onRefresh={onRefresh}
                        variant="embedded"
                        showShareButtons={false}
                        showLocation={false}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        ) : (
          <TypographyMuted className="italic text-center">
            No linked polls found
          </TypographyMuted>
        )}
        <div className="flex justify-between mt-2 px-2">
          <Button 
            onClick={onRefresh}
            variant="ghost"
            size="sm"
            className="text-xs flex items-center gap-1 text-nation-purple hover:bg-nation-purple/10"
            disabled={isLoading}
          >
            <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
            Refresh Polls
          </Button>
          <TypographySmall className="text-muted-foreground">
            {linkedPolls.length > 0 ? `Last updated: ${new Date().toLocaleTimeString()}` : ""}
          </TypographySmall>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default LinkedPollsSection;
